import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { DEFAULT_PROMO_BAR, type PromoBarSettings } from "@ckgo2/shared/types";
import { api, ApiError, type ApiResponse } from "@/api/client";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";

/**
 * Horní (promo) lišta webu — vlastní stránka s vlastním oprávněním (řez 04, H4).
 *
 * Dřív se editovala na `/homepage` jedním PUT do kategorie `homepage`, který smí jenom admin,
 * takže editor stránku viděl, vyplnil a uložení mu spadlo na 403. Teď má vlastní endpoint
 * `/api/admin/promo-bar` chráněný sdíleným `PROMO_BAR_EDIT_ROLES` — a hlavně: zapisuje
 * VÝHRADNĚ klíč `homepage.promoBar`, takže uložení lišty nemůže přepsat hero titulek ani
 * oblíbené destinace.
 */
export default function PromoBarPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /**
   * `invalid` nese endpoint tehdy, když uložená hodnota neprošla schématem (typicky odkaz na
   * cestu, která zmizela z route mapy). Formulář v takovém případě drží ŽIVOU hodnotu, ne
   * default — jinak by první uložení tiše přepsalo běžící kampaň prázdnou lištou.
   */
  const { data, isLoading } = useQuery({
    queryKey: ["promo-bar"],
    queryFn: () =>
      api.get<ApiResponse<PromoBarSettings> & { invalid?: string }>("/api/admin/promo-bar"),
  });

  // Default z kanonické konstanty schématu, ne z literálu — a načtená hodnota se přebírá
  // CELÁ (`setForm(data.data)`), ne kopírováním po polích: endpoint vrací hodnotu prohnanou
  // Zodem, takže jsou pole vždy vyplněná, a ruční výčet by čtvrté pole schématu tiše zahodil
  // (PUT by ho pak poslal bez něj → 400 bez stopy v UI).
  const [form, setForm] = useState<PromoBarSettings>(DEFAULT_PROMO_BAR);

  useEffect(() => {
    if (data?.data) setForm(data.data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put("/api/admin/promo-bar", form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["promo-bar"] }),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  const saveError = saveMutation.error;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.promoBar.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("content.promoBar.subtitle")}</p>
        </div>
        {/* Sdílený `SaveSuccessIndicator` (stránka nemá `EditPageHeader`) — vlastní
            „Uloženo" v popisku tlačítka se po prvním uložení už NIKDY nevrátilo zpátky,
            takže tlačítko natrvalo tvrdilo, že je uloženo. */}
        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn-primary"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("common.actions.save")}
          </button>
        </div>
      </div>

      <section className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("content.promoBar.sectionTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t("content.promoBar.hint")}</p>
        </div>

        {data?.invalid && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {t("content.promoBar.storedInvalid")} {data.invalid}
          </p>
        )}

        {saveError && (
          <p className="text-error-600 dark:text-error-400 text-sm">
            {saveError instanceof ApiError && saveError.message
              ? saveError.message
              : t("content.promoBar.saveFailed")}
          </p>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300"
          />
          {t("content.promoBar.enabled")}
        </label>

        <div>
          <label className="label">{t("content.promoBar.textLabel")}</label>
          <input
            value={form.text}
            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            className="input"
            maxLength={200}
          />
        </div>

        <div>
          <label className="label">{t("content.promoBar.hrefLabel")}</label>
          <input
            value={form.href}
            onChange={(e) => setForm((f) => ({ ...f, href: e.target.value }))}
            className="input"
            maxLength={500}
          />
          <p className="mt-1 text-xs text-gray-500">{t("content.promoBar.hrefHint")}</p>
        </div>

        <p className="text-xs text-gray-500">{t("content.promoBar.cacheHint")}</p>
      </section>
    </div>
  );
}
